import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { saveSeoData } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Settings, Plus, X, Flag, Globe2, Layers, Target, Save, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ALL_COUNTRIES = [
  ["AR", "Argentina"], ["MX", "México"], ["ES", "España"], ["CL", "Chile"], ["CO", "Colombia"],
  ["PE", "Perú"], ["UY", "Uruguay"], ["BR", "Brasil"], ["US", "EE.UU."], ["EC", "Ecuador"],
];

const defaultDeliverables = () => ({
  technical_audits: { target: 0, label: "Auditorías técnicas y fixes completos" },
  organic_traffic_growth: { min_pct: 0, max_pct: 0, label: "Crecimiento de tráfico orgánico web" },
  new_keywords_page1: { target: 0, label: "Nuevas keywords posicionadas en página 1" },
  ai_overview_topics: { min: 0, max: 0, label: "Aparición en AI Overview en temas clave (sostenida)" },
  quality_backlinks: { min: 0, max: 0, label: "Backlinks de calidad generados" },
  conversion_rate_improvement: { min_pct: 0, max_pct: 0, label: "Mejora de tasa de conversión" },
  blog_migration: { enabled: false, label: "Migración del blog a dominio principal" },
  portals_opened: { min: 0, label: "Revisión y apertura de portales" },
});

export default function SeoSettingsDialog({ clientId, seoData, open, onOpenChange }) {
  const qc = useQueryClient();
  const c = seoData?.compromiso || {};
  const [form, setForm] = useState({
    monitoring_started_at: c.monitoring_started_at || new Date().toISOString().slice(0, 10),
    duration_months: c.duration_months || 6,
    kpi_keywords: c.kpi_keywords || [],
    target_countries: c.target_countries || [],
    platforms: c.platforms || [],
    tech_health_goal: c.tech_health_goal || 95,
    authority_score_goal: c.authority_score_goal || 35,
    ai_visibility_goal: c.ai_visibility_goal || 60,
    deliverables: { ...defaultDeliverables(), ...(c.deliverables || {}) },
    notes: c.notes || "",
  });

  const [newKw, setNewKw] = useState("");
  const [newPlatform, setNewPlatform] = useState("");

  // Reset when seoData changes (e.g., dialog re-opens)
  useEffect(() => {
    if (open && seoData?.compromiso) {
      const cc = seoData.compromiso;
      setForm({
        monitoring_started_at: cc.monitoring_started_at || new Date().toISOString().slice(0, 10),
        duration_months: cc.duration_months || 6,
        kpi_keywords: cc.kpi_keywords || [],
        target_countries: cc.target_countries || [],
        platforms: cc.platforms || [],
        tech_health_goal: cc.tech_health_goal || 95,
        authority_score_goal: cc.authority_score_goal || 35,
        ai_visibility_goal: cc.ai_visibility_goal || 60,
        deliverables: { ...defaultDeliverables(), ...(cc.deliverables || {}) },
        notes: cc.notes || "",
      });
    }
  }, [open, seoData]);

  const save = useMutation({
    mutationFn: () => saveSeoData(clientId, { ...seoData, compromiso: form }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["seo-data", clientId] });
      toast.success("Compromiso SEO guardado");
      onOpenChange(false);
    },
    onError: () => toast.error("No se pudo guardar"),
  });

  const addKw = () => {
    const v = newKw.trim();
    if (!v || form.kpi_keywords.includes(v)) return;
    setForm({ ...form, kpi_keywords: [...form.kpi_keywords, v] });
    setNewKw("");
  };
  const removeKw = (k) => setForm({ ...form, kpi_keywords: form.kpi_keywords.filter((x) => x !== k) });

  const addPlatform = () => {
    const v = newPlatform.trim();
    if (!v || form.platforms.includes(v)) return;
    setForm({ ...form, platforms: [...form.platforms, v] });
    setNewPlatform("");
  };
  const removePlatform = (p) => setForm({ ...form, platforms: form.platforms.filter((x) => x !== p) });

  const toggleCountry = (cc) => {
    if (form.target_countries.includes(cc)) {
      setForm({ ...form, target_countries: form.target_countries.filter((x) => x !== cc) });
    } else {
      setForm({ ...form, target_countries: [...form.target_countries, cc] });
    }
  };

  const updateDeliverable = (key, patch) => {
    setForm({ ...form, deliverables: { ...form.deliverables, [key]: { ...form.deliverables[key], ...patch } } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="seo-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Settings className="h-5 w-5 text-indigo-600" />Configurar compromiso SEO</DialogTitle>
          <DialogDescription>Definí keywords KPI, países, plataformas, goals y los entregables comprometidos al cliente para el período.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* Período */}
          <Section title="Período de monitoreo">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Inicio del monitoreo</Label>
                <Input type="date" value={form.monitoring_started_at} onChange={(e) => setForm({ ...form, monitoring_started_at: e.target.value })} data-testid="seo-monitoring-start" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Duración del compromiso (meses)</Label>
                <Input type="number" min="1" max="36" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: Number(e.target.value) })} data-testid="seo-duration" />
              </div>
            </div>
          </Section>

          {/* Keywords KPI */}
          <Section title="Keywords KPI" icon={Target}>
            <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
              {form.kpi_keywords.length === 0 && <span className="text-xs text-slate-400 italic">Sin keywords aún</span>}
              {form.kpi_keywords.map((k) => (
                <span key={k} className="text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-sm font-medium inline-flex items-center gap-1.5">
                  {k}
                  <button type="button" onClick={() => removeKw(k)} className="text-indigo-400 hover:text-rose-600" data-testid={`remove-kw-${k}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="ej: creator marketing agency" value={newKw} onChange={(e) => setNewKw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKw())} className="h-9" data-testid="kw-input" />
              <Button type="button" variant="outline" size="sm" onClick={addKw} data-testid="kw-add"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </Section>

          {/* Países */}
          <Section title="Países objetivo" icon={Globe2}>
            <div className="flex flex-wrap gap-1.5">
              {ALL_COUNTRIES.map(([code, name]) => {
                const selected = form.target_countries.includes(code);
                return (
                  <button key={code} type="button" onClick={() => toggleCountry(code)} data-testid={`country-${code}`}
                    className={`text-[11px] px-2 py-0.5 rounded-sm font-medium inline-flex items-center gap-1 border transition-colors ${selected ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                    {code} <span className="opacity-70 text-[10px]">{name}</span>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Plataformas */}
          <Section title="Plataformas habilitadas" icon={Layers}>
            <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
              {form.platforms.length === 0 && <span className="text-xs text-slate-400 italic">Sin plataformas aún</span>}
              {form.platforms.map((p) => (
                <span key={p} className="text-[11px] bg-slate-50 text-slate-700 border border-slate-300 px-2 py-0.5 rounded-sm font-medium inline-flex items-center gap-1.5">
                  {p}
                  <button type="button" onClick={() => removePlatform(p)} className="text-slate-400 hover:text-rose-600"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="ej: Web, Blog, Newsletter, YouTube" value={newPlatform} onChange={(e) => setNewPlatform(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPlatform())} className="h-9" data-testid="platform-input" />
              <Button type="button" variant="outline" size="sm" onClick={addPlatform} data-testid="platform-add"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </Section>

          {/* Goals macro */}
          <Section title="Objetivos macro">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Salud técnica meta /100</Label>
                <Input type="number" min="0" max="100" value={form.tech_health_goal} onChange={(e) => setForm({ ...form, tech_health_goal: Number(e.target.value) })} data-testid="goal-tech-health" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Authority Score meta</Label>
                <Input type="number" min="0" max="100" value={form.authority_score_goal} onChange={(e) => setForm({ ...form, authority_score_goal: Number(e.target.value) })} data-testid="goal-authority" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Visibilidad IA meta %</Label>
                <Input type="number" min="0" max="100" value={form.ai_visibility_goal} onChange={(e) => setForm({ ...form, ai_visibility_goal: Number(e.target.value) })} data-testid="goal-ai-vis" />
              </div>
            </div>
          </Section>

          {/* Compromisos / Entregables */}
          <Section title={`Compromisos a ${form.duration_months || 6} meses`} icon={Flag} highlight>
            <div className="space-y-3">
              <DeliverableSingle label="Auditorías técnicas y fixes completos" suffix="auditorías"
                value={form.deliverables.technical_audits.target}
                onChange={(v) => updateDeliverable("technical_audits", { target: v })}
                testIdPrefix="del-audits" />

              <DeliverableRange label="Crecimiento de tráfico orgánico web" suffix="%"
                min={form.deliverables.organic_traffic_growth.min_pct}
                max={form.deliverables.organic_traffic_growth.max_pct}
                onChange={(min, max) => updateDeliverable("organic_traffic_growth", { min_pct: min, max_pct: max })}
                testIdPrefix="del-traffic" />

              <DeliverableSingle label="Nuevas keywords posicionadas en página 1" suffix="keywords"
                value={form.deliverables.new_keywords_page1.target}
                onChange={(v) => updateDeliverable("new_keywords_page1", { target: v })}
                testIdPrefix="del-kw" />

              <DeliverableRange label="Aparición en AI Overview (temas clave, sostenida)" suffix="temas"
                min={form.deliverables.ai_overview_topics.min}
                max={form.deliverables.ai_overview_topics.max}
                onChange={(min, max) => updateDeliverable("ai_overview_topics", { min, max })}
                testIdPrefix="del-aio" />

              <DeliverableRange label="Backlinks de calidad generados" suffix="backlinks"
                min={form.deliverables.quality_backlinks.min}
                max={form.deliverables.quality_backlinks.max}
                onChange={(min, max) => updateDeliverable("quality_backlinks", { min, max })}
                testIdPrefix="del-bl" />

              <DeliverableRange label="Mejora de tasa de conversión" suffix="%"
                min={form.deliverables.conversion_rate_improvement.min_pct}
                max={form.deliverables.conversion_rate_improvement.max_pct}
                onChange={(min, max) => updateDeliverable("conversion_rate_improvement", { min_pct: min, max_pct: max })}
                testIdPrefix="del-conv" />

              <DeliverableSingle label="Revisión y apertura de portales (mínimo)" suffix="portales"
                value={form.deliverables.portals_opened.min}
                onChange={(v) => updateDeliverable("portals_opened", { min: v })}
                testIdPrefix="del-portals" />

              <DeliverableToggle label="Migración del blog a dominio principal"
                enabled={form.deliverables.blog_migration.enabled}
                onChange={(v) => updateDeliverable("blog_migration", { enabled: v })}
                testIdPrefix="del-blog-mig" />
            </div>
          </Section>

          {/* Notas */}
          <Section title="Notas estratégicas">
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Foco del cliente, prioridades, contexto..." data-testid="seo-notes" />
          </Section>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="seo-save-btn" className="bg-slate-900 hover:bg-slate-800">
            <Save className="h-4 w-4 mr-1.5" />{save.isPending ? "Guardando..." : "Guardar compromiso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon: Icon, highlight, children }) {
  return (
    <div className={`${highlight ? "bg-indigo-50/40 border-indigo-200" : "bg-slate-50 border-slate-200"} border rounded-md p-4`}>
      <div className="flex items-center gap-1.5 mb-3">
        {Icon && <Icon className={`h-3.5 w-3.5 ${highlight ? "text-indigo-600" : "text-slate-500"}`} />}
        <h4 className={`text-[11px] uppercase tracking-wider font-semibold ${highlight ? "text-indigo-700" : "text-slate-600"}`}>{title}</h4>
      </div>
      {children}
    </div>
  );
}

function DeliverableSingle({ label, suffix, value, onChange, testIdPrefix }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-[200px]">
        <div className="text-xs font-medium text-slate-800">{label}</div>
      </div>
      <div className="flex items-center gap-1.5">
        <Input type="number" min="0" value={value || 0} onChange={(e) => onChange(Number(e.target.value))} className="h-8 w-20 text-right font-mono tabular-nums" data-testid={`${testIdPrefix}-value`} />
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{suffix}</span>
      </div>
    </div>
  );
}

function DeliverableRange({ label, suffix, min, max, onChange, testIdPrefix }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-[200px]">
        <div className="text-xs font-medium text-slate-800">{label}</div>
      </div>
      <div className="flex items-center gap-1.5">
        <Input type="number" min="0" value={min || 0} onChange={(e) => onChange(Number(e.target.value), max)} className="h-8 w-16 text-right font-mono tabular-nums" data-testid={`${testIdPrefix}-min`} />
        <span className="text-[10px] text-slate-400">a</span>
        <Input type="number" min="0" value={max || 0} onChange={(e) => onChange(min, Number(e.target.value))} className="h-8 w-16 text-right font-mono tabular-nums" data-testid={`${testIdPrefix}-max`} />
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{suffix}</span>
      </div>
    </div>
  );
}

function DeliverableToggle({ label, enabled, onChange, testIdPrefix }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-[200px]">
        <div className="text-xs font-medium text-slate-800">{label}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] uppercase tracking-wider font-semibold ${enabled ? "text-emerald-700" : "text-slate-400"}`}>{enabled ? "Sí, comprometido" : "No incluido"}</span>
        <Switch checked={enabled} onCheckedChange={onChange} data-testid={`${testIdPrefix}-switch`} />
      </div>
    </div>
  );
}
